import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const formatMoney = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const amount = Number(value);
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function DonationReceiptPopup({ donation, onClose }) {
  const receiptRef = useRef(null);
  const [isWorking, setIsWorking] = useState(false);
  const amount = donation.amount || "";
  const transactionId = donation.transactionid || "";
  const receiptNo = donation.receiptno || transactionId;
  const donationType = donation.donationTypeLabel || "";
  const donationTitle = donation.donationTitle || "";
  const paymentMethod = donation.paymentmethod || "";
  const email = donation.email || "";
  const paymentStatus = String(donation.status || "").trim().toLowerCase();
  const isPaymentSuccessful = paymentStatus === "success" || paymentStatus === "completed";
  const isPaymentPending = paymentStatus === "pending" || paymentStatus === "processing";
  const paymentStatusLabel = isPaymentSuccessful ? "Completed" : (isPaymentPending ? "Pending" : (paymentStatus ? "Failed" : ""));
  const paymentStatusIcon = isPaymentSuccessful
    ? "/images/img/tick-s.svg"
    : (isPaymentPending ? "/images/img/time-progress.svg" : "/images/img/close-circle.svg");
  const paymentStatusStyle = isPaymentSuccessful
    ? { background: "#e8f8f1", color: "#159a63" }
    : (isPaymentPending
      ? { background: "#fff4dc", color: "#9a6500" }
      : { background: "#fde9e9", color: "#b33434" });

  const createReceiptPdf = async () => {
    if (!receiptRef.current) return null;

    setIsWorking(true);
    receiptRef.current.classList.add("is-exporting-receipt");

    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        windowWidth: receiptRef.current.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [canvas.width, canvas.height],
        compress: true,
      });

      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      return pdf;
    } finally {
      receiptRef.current.classList.remove("is-exporting-receipt");
      setIsWorking(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const pdf = await createReceiptPdf();
      pdf?.save("receipt-pdf.pdf");
    } finally {
      setIsWorking(false);
    }
  };

  const handlePrintReceipt = async () => {
    try {
      const pdf = await createReceiptPdf();
      if (!pdf) return;
      pdf.autoPrint();
      window.open(pdf.output("bloburl"), "_blank");
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="receipt-popup" role="dialog" aria-modal="true" aria-label="Donation receipt">
      <div className="receipt-popup__overlay" onClick={onClose}></div>
      <div className="receipt-popup__dialog">
        <button className="receipt-popup__close" type="button" onClick={onClose} aria-label="Close receipt">
          {"×"}
        </button>
        <section className="print-screens-wrp receipt-popup__scroll">
          <div className="container">
            <div className="print-screens" ref={receiptRef}>
              <div className="ps-header">
                <div className="top-head">
                  <div className="brand">
                    <img src="/images/img/Tarahum_logo-b.png" alt="Tarahum Charity Foundation" style={{ maxWidth: "190px" }} />
                  </div>
                  <button className="print-btn" type="button" onClick={handlePrintReceipt} disabled={isWorking}>
                    {isWorking ? "Preparing..." : "Print Receipt"}
                  </button>
                </div>
              </div>

              <div className="ps-body">
                <div className="receipt-top">
                  <div className="receipt-title">
                    <h1>{"Donation Receipt"}</h1>
                    <p>
                      {"Thank you for your generosity and support."}
                      <br />
                      {"Your donation makes a real difference."}
                    </p>
                  </div>
                  <div className="receipt-info">
                    <div className="info-box">
                      <span>{"Date"}</span>
                      <strong>{formatDate(donation.dt)}</strong>
                    </div>
                    <div className="info-box">
                      <span>{"Receipt Number"}</span>
                      <strong>{receiptNo}</strong>
                    </div>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-card">
                    <div className="info-icon"><img src="/images/img/s09.png" alt="" /></div>
                    <h5>{"Amount Donated"}</h5>
                    <h3>{"AED "}{formatMoney(amount)}</h3>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><img src="/images/img/s07.png" alt="" /></div>
                    <h5>{"Donation Type"}</h5>
                    <h3>{donationType}</h3>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><img src="/images/img/s02.png" alt="" /></div>
                    <h5>{"For"}</h5>
                    <h3>{donationTitle}</h3>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><img src="/images/img/time-progress.svg" alt="" /></div>
                    <h5>{"Date & Time"}</h5>
                    <h3>{formatDateTime(donation.dt)}</h3>
                  </div>
                </div>

                <div className="payment-bar">
                  <div className="payment-item">
                    <h6>{"Transaction ID"}</h6>
                    <strong>{transactionId}</strong>
                  </div>
                  <div className="payment-item">
                    <h6>{"Payment Method"}</h6>
                    <strong>{paymentMethod}</strong>
                  </div>
                  <div className="payment-item">
                    <h6>{"Payment Status"}</h6>
                    <div className={`status status-${paymentStatusLabel.toLowerCase()}`} style={paymentStatusStyle}>
                      <img src={paymentStatusIcon} width="29" alt="" />
                      {paymentStatusLabel}
                    </div>
                  </div>
                </div>

                <div className="content-grid">
                  <div className="box">
                    <h2 className="box-title">{"About Your Donation"}</h2>
                    <p>
                      {"Your donation will help us continue our mission and deliver essential support to those in need."}
                      <br />
                      <br />
                      {"May Allah reward you for your kindness."}
                    </p>
                    <div className="signature">{"Chairman"}</div>
                    <div className="chairman">{"Tarahum Charity Foundation"}</div>
                  </div>

                  <div className="box">
                    <h2 className="box-title">{"Donation Breakdown"}</h2>
                    <div className="breakdown-row">
                      <span>{"Amount"}</span>
                      <strong>{"AED "}{formatMoney(amount)}</strong>
                    </div>
                    <div className="breakdown-row">
                      <span>{"Payment Fees"}</span>
                      <strong>{"AED 0.00"}</strong>
                    </div>
                    <div className="total-row">
                      <span>{"Total"}</span>
                      <span>{"AED "}{formatMoney(amount)}</span>
                    </div>
                  </div>
                </div>

                <div className="secure-box">
                  <div className="secure-left">
                    <div className="shield"><img src="/images/img/secure.svg" width="45" alt="" /></div>
                    <div>
                      <h3>{"100% Secure"}</h3>
                      <p>
                        {"Your donation is secure and encrypted."}
                        <br />
                        {"This is an official receipt for your records."}
                      </p>
                    </div>
                  </div>
                  <div className="mail-text">
                    {"This receipt has been sent to"}
                    <br />
                    <strong>{email}</strong>
                  </div>
                </div>

                <div className="bottom-grid">
                  <div className="quote-box">
                    <p>{"\"The example of those who spend their wealth in the way of Allah is like a seed that grows seven spikes; in each spike is a hundred grains.\""}</p>
                    <strong>{"(Al-Baqarah 2:261)"}</strong>
                  </div>
                  <div className="contact-box">
                    <ul>
                      <li><i className="fa-solid fa-headset"></i>{" 042848000"}</li>
                      <li><i className="fas fa-globe"></i>{" www.tarahum.ae"}</li>
                      <li><i className="fa-regular fa-envelope"></i>{" info@tarahum.ae"}</li>
                      <li><i className="fa-solid fa-location-dot"></i>{" Al Tay, Al Khawaneej, DXB 78000"}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="footer-bar">
                <div>{"Thank you for being a part of the change."}</div>
                <div className="footer-actions">
                  <button className="footer-btn" type="button" onClick={handleDownloadPdf} disabled={isWorking}>
                    {isWorking ? "Preparing..." : "Download PDF"}
                  </button>
                  <button className="footer-btn" type="button" onClick={handlePrintReceipt} disabled={isWorking}>
                    {isWorking ? "Preparing..." : "Print Receipt"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
